import React, { useState, useEffect } from "react";
import {
  Box,
  Grid,
  Typography,
  TextField,
  Button,
  Divider,
  MenuItem,
  CircularProgress,
} from "@mui/material";
import money from "../../assets/img/money.png";
import qr from "../../assets/img/qr.png";
import PaymentNav from "./PaymentNav";
import { useLocation } from "react-router-dom";
import OrderSuccessModal from "../../components/modal/OrderSuccessModal";
import { sendSMS, createBulkOrder } from "../../api/Api"; // Updated to import createBulkOrder

function generateOrderId(orderName, quantity, price) {
  const timestamp = new Date().getTime();
  const randomNum = Math.floor(Math.random() * 1000);
  const sanitizedOrderName = orderName
    .replace(/\s+/g, "")
    .toUpperCase()
    .slice(0, 5);
  return `${sanitizedOrderName}-${quantity}-${price}-${timestamp}-${randomNum}`;
}

function PaymentSection() {
  const location = useLocation();
  const { orderData } = location.state || {};
  const [orderId, setOrderId] = useState("");
  const [isConfirmDisabled, setConfirmDisabled] = useState(true);
  const [isPaymentProcessing, setPaymentProcessing] = useState(false);
  const [isModalOpen, setModalOpen] = useState(false);
  const [paymentMethod, setPaymentMethod] = useState("Cash");
  const [paymentStatus, setPaymentStatus] = useState("Pending");
  const [isLoading, setIsLoading] = useState(false); // Loading state

  useEffect(() => {
    if (orderData) {
      const newOrderId = generateOrderId(
        orderData.item,
        orderData.quantity,
        orderData.price
      );
      setOrderId(newOrderId);
    }
  }, [orderData]);

  const handlePayNow = () => {
    setPaymentProcessing(true);
    setTimeout(() => {
      setPaymentProcessing(false);
      setConfirmDisabled(false);
    }, 3000);
  };

  const handleConfirm = async () => {
    setIsLoading(true); // Set loading to true when placing the order
    try {
      const sanitizedOrderData = {
        name: orderData?.fullName || "",
        contactNo: orderData?.contactNo || "",
        itemName: orderData?.item || "",
        quantity: orderData?.quantity || 0,
        deliveryAddress: orderData?.deliveryAddress || "",
        expectedDeliveryDate: orderData?.deliveryDate || "",
        area: orderData?.area || "",
        totalAmount: orderData?.price * orderData?.quantity || 0,
        paymentMethod,
      };

      const response = await createBulkOrder(sanitizedOrderData);

      // Checking for a successful response
      if (response) {
        const smsPayload = {
          to: "+918984140247",
          message: `${sanitizedOrderData.name} placed an order.
      ${sanitizedOrderData.itemName}
      Qty: ${sanitizedOrderData.quantity}
      Contact: ${sanitizedOrderData.contactNo}
      EOD: ${sanitizedOrderData.expectedDeliveryDate}
      Payment Mode: ${paymentMethod}
      Area: ${sanitizedOrderData.area}
      Address: ${sanitizedOrderData.deliveryAddress}`,
        };

        // Sending SMS
        await sendSMS(smsPayload);
        setModalOpen(true);
      } else {
        console.error("Bulk order creation failed:", response.message);
      }
    } catch (error) {
      console.error("Error in confirming bulk order:", error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleCloseModal = () => {
    setModalOpen(false);
  };

  return (
    <>
      <Box sx={{ mb: 4 }}>
        <PaymentNav />
      </Box>
      <Box sx={{ padding: 4 }}>
        <Grid container justifyContent="center">
          <Grid item xs={12} sm={10} md={12} sx={{ marginTop: "5vh" }}>
            <Box
              sx={{
                display: "flex",
                flexDirection: { xs: "column", md: "row" },
                backgroundColor: "#ffffff",
                borderRadius: 2,
                width: "100%",
                overflow: "hidden",
                gap: "5rem",
              }}
            >
              {/* Left Box - Order Details */}
              <Box
                sx={{
                  flex: 1,
                  padding: 4,
                  backgroundColor: "#F5F6F7",
                  width: "40%",
                  minHeight: "400px",
                }}
              >
                <Typography
                  variant="h5"
                  sx={{ color: "#114C78", marginBottom: 3 }}
                >
                  Order Details
                </Typography>
                <Divider sx={{ marginBottom: 4 }} />
                <Grid container spacing={2}>
                  <Grid item xs={12} md={6}>
                    <Typography sx={{ fontSize: "1.1rem", padding: "5px 0" }}>
                      Customer Name:{" "}
                      <span style={{ color: "#208EE1" }}>
                        {orderData.fullName}
                      </span>
                    </Typography>
                    <Typography sx={{ fontSize: "1.1rem", padding: "5px 0" }}>
                      Contact No:{" "}
                      <span style={{ color: "#208EE1" }}>
                        {orderData.contactNo}
                      </span>
                    </Typography>
                    <Typography sx={{ fontSize: "1.1rem", padding: "5px 0" }}>
                      Item:{" "}
                      <span style={{ color: "#208EE1" }}>{orderData.item}</span>
                    </Typography>
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <Typography sx={{ fontSize: "1.1rem", padding: "5px 0" }}>
                      Order No:{" "}
                      <span style={{ color: "#208EE1" }}>10102024</span>
                    </Typography>
                    <Typography sx={{ fontSize: "1.1rem", padding: "5px 0" }}>
                      Delivery Date:{" "}
                      <span style={{ color: "#208EE1" }}>
                        {orderData.deliveryDate}
                      </span>
                    </Typography>
                    <Typography sx={{ fontSize: "1.1rem", padding: "5px 0" }}>
                      Quantity:{" "}
                      <span style={{ color: "#208EE1" }}>
                        {orderData.quantity}
                      </span>
                    </Typography>
                    <Typography sx={{ fontSize: "1.1rem", padding: "5px 0" }}>
                      Total Amount:{" "}
                      <span style={{ color: "#208EE1" }}>
                        {(() => {
                          console.log("Price:", orderData.price);
                          console.log("Quantity:", orderData.quantity);
                          return orderData.price * orderData.quantity;
                        })()}
                      </span>
                    </Typography>
                  </Grid>
                </Grid>
                <Box mt={3}>
                  <Typography sx={{ fontSize: "1.1rem", padding: "5px 0" }}>
                    Delivery Address:{" "}
                    <span style={{ color: "#208EE1" }}>
                      {orderData.deliveryAddress}
                    </span>
                  </Typography>
                </Box>
              </Box>

              {/* Right Box - Payment Details */}
              <Box
                sx={{
                  flex: 1,
                  padding: 4,
                  backgroundColor: "#F5F6F7",
                  minHeight: "400px",
                  width: "55%",
                }}
              >
                <Typography
                  variant="h5"
                  sx={{ color: "#114C78", marginBottom: 3 }}
                >
                  Payment Details
                </Typography>
                <Divider sx={{ marginBottom: 4 }} />

                <Grid container spacing={2}>
                  <Grid item xs={12} md={6} sx={{ padding: "10px" }}>
                    <TextField
                      select
                      fullWidth
                      label="Select Payment Method"
                      value={paymentMethod}
                      onChange={(e) => setPaymentMethod(e.target.value)}
                      variant="outlined"
                      sx={{ marginBottom: 5 }}
                    >
                      <MenuItem value="Cash">Cash</MenuItem>
                      <MenuItem value="UPI">UPI</MenuItem>
                    </TextField>
                    <Typography variant="subtitle1">
                      Payment Status:{" "}
                      <span
                        style={{
                          color:
                            paymentStatus === "Success" ? "#4CAF50" : "#FF9800",
                        }}
                      >
                        {paymentStatus}
                      </span>
                    </Typography>
                  </Grid>
                  <Grid
                    item
                    xs={12}
                    md={6}
                    sx={{ textAlign: "center", padding: "10px" }}
                  >
                    <Box
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        backgroundColor: "#f1f1f1",
                        height: "120px",
                        borderRadius: "8px",
                        marginBottom: 8,
                        padding: "10px",
                      }}
                    >
                      {paymentMethod === "Cash" ? (
                        <img
                          src={money}
                          alt="Cash Icon"
                          style={{ width: "55%", height: "100%" }}
                        />
                      ) : (
                        <img
                          src={qr}
                          alt="Cash Icon"
                          style={{ width: "55%", height: "100%" }}
                        />
                      )}
                    </Box>
                    <Button
                      variant="contained"
                      fullWidth
                      sx={{
                        maxWidth: "180px",
                        backgroundColor:
                          paymentMethod === "Cash" ? "#297643" : "#208EE1",
                        color: "#FFFFFF",
                        padding: "10px",
                        fontSize: "14px",
                        fontWeight: "500",
                        textTransform: "capitalize",
                        borderRadius: "0px",
                        "&:hover": {
                          backgroundColor:
                            paymentMethod === "Cash" ? "#388E3C" : "#1A6EB5",
                        },
                      }}
                      onClick={handleConfirm}
                      disabled={isPaymentProcessing}
                    >
                      {isLoading ? (
                        <CircularProgress size={24} color="inherit" />
                      ) : (
                        "Confirm"
                      )}
                    </Button>
                  </Grid>
                </Grid>
              </Box>
            </Box>
          </Grid>
        </Grid>
      </Box>
      <OrderSuccessModal isOpen={isModalOpen} onClose={handleCloseModal} />
    </>
  );
}

export default PaymentSection;
